import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createCapture } from "../lib/offlineCaptures";
import { untitledCaptureName } from "../lib/captureNaming";
import { formatApiErrorDetail } from "../lib/api";

const ACCEPTED_FILE_TYPES = (file) => file.type.startsWith("image/") || file.type === "application/pdf";

// Handles file upload (single/multi), clipboard paste, and drag-and-drop
// import flows for the dashboard. Uploaded/pasted screenshots and PDFs are
// saved directly (no annotation step) — annotation happens later when the
// user opens a saved image capture from the dashboard (PDFs aren't
// annotatable).
export function useCaptureUpload({ selectedTagId, loadCaptures, loadTags }) {
  const { t, i18n } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const readAsDataURL = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });

  const refreshAfterImport = () => {
    loadCaptures(selectedTagId);
    loadTags();
  };

  const bulkImportFiles = async (files) => {
    let successCount = 0;
    let lastError = null;
    for (const file of files) {
      try {
        const dataUrl = await readAsDataURL(file);
        const title = file.name.replace(/\.[^/.]+$/, "") || untitledCaptureName();
        const payload = { title, tags: selectedTagId ? [selectedTagId] : [], title_language: i18n.language };
        if (file.type === "application/pdf") payload.pdf_data = dataUrl;
        else payload.image_data = dataUrl;
        await createCapture(payload);
        successCount += 1;
      } catch (e) {
        // Keep importing the rest of the batch even if one file (e.g. a
        // PDF over the page limit) fails - one bad file shouldn't block
        // everything else the user selected.
        lastError = e;
      }
    }
    if (successCount > 0) {
      toast.success(successCount > 1 ? t("toast.importedManySuccess", { count: successCount }) : t("toast.importedOneSuccess"));
      refreshAfterImport();
    }
    if (lastError) {
      toast.error(formatApiErrorDetail(lastError.response?.data?.detail) || t("toast.uploadFailedError"));
    }
  };

  const handleUploadFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    bulkImportFiles(files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []).filter(ACCEPTED_FILE_TYPES);
    if (files.length === 0) {
      toast.error(t("toast.dropNonImageError"));
      return;
    }
    handleUploadFiles(files);
  };

  // Clipboard paste (e.g. Ctrl+V a screenshot or a PDF copied from the OS)
  // creates a new capture the same way a drag-drop would. Skipped while
  // focused in a text input/textarea/contenteditable (the Notes rich text
  // editor) so a normal text paste there isn't hijacked - this only ever
  // fires for actual pasted files, never plain text, but staying out of
  // the way of in-note editing keeps the behavior unsurprising.
  useEffect(() => {
    const handlePasteEvent = (e) => {
      const active = document.activeElement;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)) return;
      const files = Array.from(e.clipboardData?.files || []).filter(ACCEPTED_FILE_TYPES);
      if (files.length === 0) return;
      handleUploadFiles(files);
    };
    document.addEventListener("paste", handlePasteEvent);
    return () => document.removeEventListener("paste", handlePasteEvent);
  }, [selectedTagId]);

  return {
    handleUploadFiles,
    isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop,
  };
}
